import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Bot, Zap } from 'lucide-react';

const Slide3Solution = () => {
    return (
        <div className="w-full h-full bg-background p-8 lg:p-16 flex flex-col relative overflow-hidden">
            {/* Background Grid Texture */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
            />
            <div className="max-w-6xl mx-auto w-full h-full flex flex-col justify-center">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl lg:text-6xl font-bold mb-4 tracking-tight">Current Platforms Offer <span className="text-muted-foreground">Visibility, Not Optimization.</span></h2>
                    <p className="text-xl lg:text-2xl text-muted-foreground">Users are informed but not financially better off.</p>
                </motion.div>

                {/* 3-Column Matrix */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch pt-4">
                    {/* Column 1: Existing */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card border border-border/50 p-8 rounded-3xl flex flex-col items-center text-center hover:bg-card/80 transition-all duration-300 group"
                    >
                        <div className="w-16 h-16 bg-muted/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <BarChart3 className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Dashboards</h3>
                        <p className="text-[10px] text-muted-foreground mb-6 font-mono tracking-widest uppercase">Groww, CRED, Walnut, etc.</p>

                        <div className="w-full space-y-6 mb-8 text-left">
                            <div className="space-y-3">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-red-500 font-black">Constraints & Gaps</p>
                                <ul className="text-sm lg:text-base text-muted-foreground space-y-3">
                                    <li className="flex gap-2 leading-tight">
                                        <span className="text-red-500/50 mt-1 font-bold">✕</span>
                                        <span><strong className="text-gray-300">Action Gap:</strong> Data visibility without any execution.</span>
                                    </li>
                                    <li className="flex gap-2 leading-tight">
                                        <span className="text-red-500/50 mt-1 font-bold">✕</span>
                                        <span><strong className="text-gray-300">Fatigue:</strong> Overwhelming alerts without "Next Steps".</span>
                                    </li>
                                    <li className="flex gap-2 leading-tight">
                                        <span className="text-red-500/50 mt-1 font-bold">✕</span>
                                        <span><strong className="text-gray-300">Siloed Data:</strong> Ignores taxation & emergency needs.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="mt-auto px-6 py-2 bg-muted/10 rounded-full text-[10px] text-muted-foreground border border-muted/20 font-black tracking-[0.2em] uppercase">
                            Passive
                        </div>
                    </motion.div>

                    {/* Column 2: Robo-Advisors */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-card border border-border/50 p-8 rounded-3xl flex flex-col items-center text-center hover:bg-card/80 transition-all duration-300 group"
                    >
                        <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Bot className="w-8 h-8 text-blue-400" />
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Robo-Advisors</h3>
                        <p className="text-[10px] text-muted-foreground mb-6 font-mono tracking-widest uppercase">Traditional WealthTech</p>

                        <div className="w-full space-y-6 mb-8 text-left">
                            <div className="space-y-3">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-red-500 font-black">Constraints & Gaps</p>
                                <ul className="text-sm lg:text-base text-muted-foreground space-y-3">
                                    <li className="flex gap-2 leading-tight">
                                        <span className="text-red-500/50 mt-1 font-bold">✕</span>
                                        <span><strong className="text-gray-300">Static Logic:</strong> Doesn't adapt to daily shifts.</span>
                                    </li>
                                    <li className="flex gap-2 leading-tight">
                                        <span className="text-red-500/50 mt-1 font-bold">✕</span>
                                        <span><strong className="text-gray-300">Generic:</strong> Broad profiles ignore personal flow.</span>
                                    </li>
                                    <li className="flex gap-2 leading-tight">
                                        <span className="text-red-500/50 mt-1 font-bold">✕</span>
                                        <span><strong className="text-gray-300">Black Box:</strong> Zero transparency on rebalancing logic.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="mt-auto px-6 py-2 bg-blue-500/10 rounded-full text-[10px] text-blue-400 border border-blue-500/20 font-black tracking-[0.2em] uppercase">
                            Static
                        </div>
                    </motion.div>

                    {/* Column 3: ZenCap */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-card border-2 border-primary p-8 rounded-3xl flex flex-col items-center text-center relative shadow-[0_0_60px_rgba(0,255,0,0.2)] z-10"
                    >
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-black text-[10px] font-black px-5 py-2 rounded-full uppercase tracking-[0.3em] shadow-xl">
                            THE SOLUTION
                        </div>
                        <div className="w-20 h-20 bg-primary/20 rounded-2xl flex items-center justify-center mb-6 shadow-[0_0_20px_rgba(0,255,0,0.3)] animate-pulse">
                            <Zap className="w-10 h-10 text-primary" />
                        </div>
                        <h3 className="text-3xl font-black mb-1 text-white tracking-tight text-center">ZenCap</h3>
                        <p className="text-[10px] text-primary font-mono mb-6 uppercase tracking-[0.3em] font-bold">Autonomous ADI</p>

                        <div className="w-full space-y-5 mb-10 text-left">
                            <div className="space-y-4">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-black">How ADI Fixes This</p>
                                <ul className="text-xs lg:text-base text-white space-y-3 font-medium">
                                    <li className="flex gap-3 group/item">
                                        <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 shrink-0" />
                                        <span><strong className="text-primary font-bold">Real-time Nudges:</strong> Suggests daily optimized wealth moves.</span>
                                    </li>
                                    <li className="flex gap-3 group/item">
                                        <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 shrink-0" />
                                        <span><strong className="text-primary font-bold">Adaptive Execution:</strong> Dynamics risk rebalancing on the fly.</span>
                                    </li>
                                    <li className="flex gap-3 group/item">
                                        <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 shrink-0" />
                                        <span><strong className="text-primary font-bold">Transparent AI:</strong> Every suggestion comes with clear reasoning.</span>
                                    </li>
                                    <li className="flex gap-3 group/item text-xs text-primary/80 italic pl-1.5 border-l border-primary/30">
                                        <span>"ADI turns static data into active wealth."</span>
                                    </li>
                                    <li className="flex gap-3 group/item text-xs text-primary/80 italic pl-1.5 border-l border-primary/30">
                                        <span>"Under supervision of SEBI Registrated RIA."</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="mt-auto w-full py-3 bg-primary rounded-xl text-lg text-black font-black shadow-lg shadow-primary/30 active:scale-95 transition-all uppercase tracking-widest">
                            ACTIVE
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Slide3Solution;